import React from 'react';
import { ScrollView, View } from 'react-native';

import tw from '@/styles/tailwind';
import Text from '@/components/Text';

// ───────────────────────────────────────────────────────────────────────────
// Reporte "Diferencia balanzas" (por comunidad, por año).
// Compara, mes a mes, dos fuentes con dos métricas (Inventario, Conservación):
//   • Balanza  → proviene de la balanza de ingresos (reporte principal)
//   • Ingreso  → proviene de las cuentas contables (Inventario 5110004 / Conservación 5110006)
// La Diferencia = Balanza − Ingreso se CALCULA (no se hardcodea): al cablear las
// dos fuentes reales, la diferencia se recalcula sola.
//
// TODO(backend): reemplazar BALANZA/INGRESO demo por datos reales (por mes/comunidad).
// ───────────────────────────────────────────────────────────────────────────

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// Una celda puede no tener dato todavía (mes sin captura) → null = vacío.
type Cell = number | null;
type MonthMetrics = { inventario: Cell; conservacion: Cell };

// ─── Fuentes DEMO (Ene–Mar con datos; resto vacío) ───────────────────────────
const BALANZA: MonthMetrics[] = [
  { inventario: 73852.89, conservacion: 60547.45 }, // Enero
  { inventario: 68449.02, conservacion: 58708.59 }, // Febrero
  { inventario: 72652.03, conservacion: 27448.01 }, // Marzo
  ...Array(9).fill({ inventario: null, conservacion: null }),
];

const INGRESO: MonthMetrics[] = [
  { inventario: 73800.0, conservacion: 94562.01 }, // Enero
  ...Array(11).fill({ inventario: null, conservacion: null }),
];

// ─── Cálculo de la diferencia (Balanza − Ingreso) ────────────────────────────
// Si ambas fuentes están vacías en el mes → null (celda en blanco).
const diff = (a: Cell, b: Cell): Cell => (a == null && b == null ? null : (a ?? 0) - (b ?? 0));

const DIFERENCIA: MonthMetrics[] = MONTHS.map((_, i) => ({
  inventario: diff(BALANZA[i].inventario, INGRESO[i].inventario),
  conservacion: diff(BALANZA[i].conservacion, INGRESO[i].conservacion),
}));

const sum = (rows: MonthMetrics[], key: keyof MonthMetrics) =>
  rows.reduce((acc, r) => acc + (r[key] ?? 0), 0);

const num = (n: Cell) =>
  n == null ? '' : n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Sub-tabla (Balanza / Ingreso / Diferencia) ──────────────────────────────
function MiniTable({
  title,
  subInventario,
  subConservacion,
  rows,
  signed,
}: {
  title: string;
  subInventario?: string;
  subConservacion?: string;
  rows: MonthMetrics[];
  signed?: boolean;
}) {
  const totalInv = sum(rows, 'inventario');
  const totalCons = sum(rows, 'conservacion');

  const color = (v: Cell) =>
    signed && typeof v === 'number'
      ? v < 0 ? 'text-red-600' : v > 0 ? 'text-green-700' : 'text-gray-700'
      : 'text-gray-700';

  return (
    <View style={tw`bg-white rounded-xl border border-gray-100 mr-4 mb-4 overflow-hidden`}>
      {/* Título de la tabla */}
      <View style={tw`bg-secondary/10 px-3 py-2`}>
        <Text style={tw`text-secondary font-700 text-center text-sm`}>{title}</Text>
      </View>

      {/* Encabezado de columnas */}
      <View style={tw`row border-b border-gray-200`}>
        <View style={tw`w-24 px-3 py-2`} />
        <View style={tw`w-32 px-3 py-2 items-end`}>
          <Text style={tw`text-gray-600 font-600 text-xs`}>Inventario</Text>
          {!!subInventario && <Text style={tw`text-gray-300 text-[10px]`}>{subInventario}</Text>}
        </View>
        <View style={tw`w-32 px-3 py-2 items-end`}>
          <Text style={tw`text-gray-600 font-600 text-xs`}>Conservación</Text>
          {!!subConservacion && <Text style={tw`text-gray-300 text-[10px]`}>{subConservacion}</Text>}
        </View>
      </View>

      {/* Filas por mes */}
      {MONTHS.map((m, i) => (
        <View key={m} style={tw.style(`row`, i % 2 === 1 && `bg-gray-50/50`)}>
          <View style={tw`w-24 px-3 py-1.5`}>
            <Text style={tw`text-gray-500 text-xs`}>{m}</Text>
          </View>
          <View style={tw`w-32 px-3 py-1.5 items-end`}>
            <Text style={tw.style(`text-xs`, color(rows[i].inventario))}>{num(rows[i].inventario)}</Text>
          </View>
          <View style={tw`w-32 px-3 py-1.5 items-end`}>
            <Text style={tw.style(`text-xs`, color(rows[i].conservacion))}>{num(rows[i].conservacion)}</Text>
          </View>
        </View>
      ))}

      {/* Totales */}
      <View style={tw`row border-t-2 border-secondary/40 bg-gray-50`}>
        <View style={tw`w-24 px-3 py-2`}>
          <Text style={tw`text-secondary font-700 text-xs`}>Total</Text>
        </View>
        <View style={tw`w-32 px-3 py-2 items-end`}>
          <Text style={tw.style(`text-xs font-700`, signed ? color(totalInv) : `text-gray-900`)}>{num(totalInv)}</Text>
        </View>
        <View style={tw`w-32 px-3 py-2 items-end`}>
          <Text style={tw.style(`text-xs font-700`, signed ? color(totalCons) : `text-gray-900`)}>{num(totalCons)}</Text>
        </View>
      </View>
    </View>
  );
}

export default function DiferenciaBalanzas({ communityId, date }: { communityId?: string; date?: Date }) {
  return (
    <View style={tw`px-4 pb-8`}>
      <Text style={tw`text-gray-400 mb-4`}>
        Diferencia entre la balanza y el ingreso contable, por mes.
      </Text>

      {/* Las 3 tablas lado a lado (scroll horizontal si no caben) */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={tw`row`}>
          <MiniTable title="Balanza" rows={BALANZA} />
          <MiniTable
            title="Ingreso"
            subInventario="5110004"
            subConservacion="5110006"
            rows={INGRESO}
          />
          <MiniTable title="Diferencia" rows={DIFERENCIA} signed />
        </View>
      </ScrollView>

      <Text style={tw`text-gray-100 text-xs mt-2`}>
        Datos demo · Diferencia calculada (Balanza − Ingreso) · pendiente de backend · communityId: {communityId ?? '—'}
      </Text>
    </View>
  );
}
